import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye,
  DollarSign,
  Calendar,
  TrendingUp,
  Percent
} from 'lucide-react';
import { 
  getAllCampaigns, 
  createCampaign, 
  updateCampaign, 
  deleteCampaign 
} from '../services/api';
import { FundraisingCampaign } from '../types';

const CampaignsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 20px 12px 45px;
  border: 2px solid #e1e5e9;
  border-radius: 25px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  size: 20px;
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  }
`;

const CampaignsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const CampaignCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    border-color: #667eea;
  }
`;

const CampaignHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const CampaignInfo = styled.div`
  flex: 1;
`;

const CampaignTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
`;

const CampaignDescription = styled.p`
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 15px;
`;

const ProgressContainer = styled.div`
  margin-bottom: 15px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e1e5e9;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const ProgressFill = styled.div<{ $percentage: number }>`
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  width: ${props => Math.min(props.$percentage, 100)}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
`;

const CampaignStats = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  color: #666;
`;

const CampaignActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ $variant: 'view' | 'edit' | 'delete' }>`
  width: 35px;
  height: 35px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  ${props => {
    switch (props.$variant) {
      case 'view':
        return `
          background: #e8f5e8;
          color: #2e7d32;
          
          &:hover {
            background: #c8e6c9;
          }
        `;
      case 'edit':
        return `
          background: #e3f2fd;
          color: #1976d2;
          
          &:hover {
            background: #bbdefb;
          }
        `;
      case 'delete':
        return `
          background: #ffebee;
          color: #d32f2f;
          
          &:hover {
            background: #ffcdd2;
          }
        `;
    }
  }}
`;

const Modal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
    }
  ` : `
    background: #f8f9fa;
    color: #333;
    border: 2px solid #e1e5e9;
    
    &:hover {
      background: #e9ecef;
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const Campaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<FundraisingCampaign[]>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState<FundraisingCampaign[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<FundraisingCampaign | null>(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    targetAmount: 0,
    startDate: '',
    endDate: '',
    isActive: true
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  useEffect(() => {
    const filtered = campaigns.filter(campaign =>
      campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCampaigns(filtered);
  }, [campaigns, searchTerm]);

  const loadCampaigns = async () => {
    try {
      const campaignsData = await getAllCampaigns();
      setCampaigns(campaignsData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setLoading(false);
    }
  };

  const handleAddCampaign = () => {
    setEditingCampaign(null);
    setFormData({
      title: '',
      description: '',
      targetAmount: 0,
      startDate: '',
      endDate: '',
      isActive: true
    });
    setIsModalOpen(true);
  };

  const handleEditCampaign = (campaign: FundraisingCampaign) => {
    setEditingCampaign(campaign);
    setFormData({
      title: campaign.title,
      description: campaign.description,
      targetAmount: campaign.targetAmount,
      startDate: (() => {
        try {
          if (campaign.startDate && typeof campaign.startDate === 'object' && 'toDate' in campaign.startDate) {
            return campaign.startDate.toDate().toISOString().split('T')[0];
          }
          const d = campaign.startDate instanceof Date ? campaign.startDate : new Date(campaign.startDate);
          return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
        } catch { return ''; }
      })(),
      endDate: (() => {
        try {
          if (campaign.endDate && typeof campaign.endDate === 'object' && 'toDate' in campaign.endDate) {
            return campaign.endDate.toDate().toISOString().split('T')[0];
          }
          const d = campaign.endDate instanceof Date ? campaign.endDate : new Date(campaign.endDate);
          return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
        } catch { return ''; }
      })(),
      isActive: campaign.isActive
    });
    setIsModalOpen(true);
  };

  const handleSaveCampaign = async () => {
    try {
      if (editingCampaign) {
        await updateCampaign(editingCampaign.id, {
          ...formData,
          startDate: formData.startDate ? new Date(formData.startDate) : undefined,
          endDate: formData.endDate ? new Date(formData.endDate) : undefined
        });
      } else {
        await createCampaign({
          ...formData,
          startDate: formData.startDate ? new Date(formData.startDate) : new Date(),
          endDate: formData.endDate ? new Date(formData.endDate) : undefined,
          createdBy: 'current-user-id' // This should come from auth context
        });
      }
      await loadCampaigns();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving campaign:', error);
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק את הקמפיין?')) {
      try {
        await deleteCampaign(campaignId);
        await loadCampaigns();
      } catch (error) {
        console.error('Error deleting campaign:', error);
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const getProgressPercentage = (current: number, target: number) => {
    if (target === 0) return 0;
    return Math.round((current / target) * 100);
  };

  if (loading) {
    return (
      <CampaignsContainer>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          טוען נתונים...
        </div>
      </CampaignsContainer>
    );
  }

  return (
    <CampaignsContainer>
      <Header>
        <Title>
          <DollarSign size={28} />
          קמפיינים
        </Title>
        <SearchBar>
          <SearchInput
            type="text"
            placeholder="חיפוש קמפיין..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchIcon size={20} />
        </SearchBar>
        <AddButton onClick={handleAddCampaign}>
          <Plus size={20} />
          הוסף קמפיין
        </AddButton>
      </Header>

      {filteredCampaigns.length === 0 ? (
        <EmptyState>
          <DollarSign size={48} style={{ marginBottom: '20px', opacity: 0.5 }} />
          <h3>אין קמפיינים במערכת</h3>
          <p>לחץ על "הוסף קמפיין" כדי להתחיל</p>
        </EmptyState>
      ) : (
        <CampaignsGrid>
          {filteredCampaigns.map((campaign) => (
            <CampaignCard key={campaign.id}>
              <CampaignHeader>
                <CampaignInfo>
                  <CampaignTitle>{campaign.title}</CampaignTitle>
                  <CampaignDescription>{campaign.description}</CampaignDescription>
                  
                  <ProgressContainer>
                    <ProgressBar>
                      <ProgressFill $percentage={getProgressPercentage(campaign.currentAmount, campaign.targetAmount)} />
                    </ProgressBar>
                    <ProgressText>
                      <span>{formatCurrency(campaign.currentAmount)}</span>
                      <span>{formatCurrency(campaign.targetAmount)}</span>
                    </ProgressText>
                  </ProgressContainer>

                  <CampaignStats>
                    <StatItem>
                      <TrendingUp size={16} />
                      {getProgressPercentage(campaign.currentAmount, campaign.targetAmount)}%
                    </StatItem>
                    <StatItem>
                    <Calendar size={16} />
                      {(() => {
                        try {
                          let d;
                          if (campaign.startDate && typeof campaign.startDate === 'object' && 'toDate' in campaign.startDate) {
                            d = campaign.startDate.toDate();
                          } else if (campaign.startDate instanceof Date) {
                            d = campaign.startDate;
                          } else {
                            d = new Date(campaign.startDate);
                          }
                          return isNaN(d.getTime()) ? 'לא מוגדר' : d.toLocaleDateString('he-IL');
                        } catch { return 'לא מוגדר'; }
                      })()}
                    </StatItem>
                  </CampaignStats>
                </CampaignInfo>
                <CampaignActions>
                  <ActionButton
                    $variant="view"
                    onClick={() => window.location.href = `/campaigns/${campaign.id}`}
                    title="צפה בקמפיין"
                  >
                    <Eye size={16} />
                  </ActionButton>
                  <ActionButton
                    $variant="edit"
                    onClick={() => handleEditCampaign(campaign)}
                    title="ערוך"
                  >
                    <Edit size={16} />
                  </ActionButton>
                  <ActionButton
                    $variant="delete"
                    onClick={() => handleDeleteCampaign(campaign.id)}
                    title="מחק"
                  >
                    <Trash2 size={16} />
                  </ActionButton>
                </CampaignActions>
              </CampaignHeader>
            </CampaignCard>
          ))}
        </CampaignsGrid>
      )}

      <Modal isOpen={isModalOpen} onClick={() => setIsModalOpen(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              {editingCampaign ? 'ערוך קמפיין' : 'הוסף קמפיין חדש'}
            </ModalTitle>
            <CloseButton onClick={() => setIsModalOpen(false)}>
              ×
            </CloseButton>
          </ModalHeader>

          <FormGroup>
            <Label>שם הקמפיין *</Label>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>תיאור הקמפיין *</Label>
            <TextArea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="תיאור המטרה של הקמפיין..."
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>סכום מטרה (₪) *</Label>
            <Input
              type="number"
              value={formData.targetAmount}
              onChange={(e) => setFormData({ ...formData, targetAmount: Number(e.target.value) })}
              required
              min="0"
            />
          </FormGroup>

          <FormGroup>
            <Label>תאריך התחלה</Label>
            <Input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <Label>תאריך סיום</Label>
            <Input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            />
          </FormGroup>

          <ButtonGroup>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              ביטול
            </Button>
            <Button variant="primary" onClick={handleSaveCampaign}>
              {editingCampaign ? 'עדכן' : 'הוסף'}
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>
    </CampaignsContainer>
  );
};

export default Campaigns;
